import { AdAccountDTO, GoogleAdsAdAccountDTO } from 'api-models'

/**
 * What to call an ad account on screen.
 *
 * The stored `name` comes from Google's `customer.descriptive_name`, which is
 * optional — accounts that never set one arrive as an EMPTY STRING (protobuf
 * string getters return "" when unset), and creation writes it through without
 * a fallback while `details.name` gets one. So `adAccount.name` is legitimately
 * blank for real accounts, and anything rendering it raw shows nothing at all:
 * the breadcrumb ad-account selector reads as unselected, and identity cards
 * lose their title while still showing the number underneath.
 *
 * The account number is the right stand-in: it is what Google Ads itself shows
 * for an account with no descriptive name, and it is the value users read out
 * when identifying an account to support.
 *
 * Display only — never write the result back. The top-level `name` is
 * user-editable (ad account settings), so a persisted fallback would be
 * indistinguishable from a name someone chose.
 */
export const adAccountLabel = (
	adAccount: Pick<AdAccountDTO, 'name'> & {
		details?: Partial<GoogleAdsAdAccountDTO['details']> | null
	}
): string =>
	adAccount.name?.trim() ||
	adAccount.details?.account_number?.trim() ||
	'Unnamed account'
